import * as ActionTypes from '../actions/types';
import xs from 'xstream';
import * as actions from '../actions';
import GENERAL_FUNCTIONS from '../utils/functions/functions';
import sampleCombine from 'xstream/extra/sampleCombine';
import delay from 'xstream/extra/delay';
import HEADER_FUNCTIONS from '../utils/functions/headers';
import authentication from '../utils/auth/react-azure-adb2c';
import { syncTranslationsFromRedux } from '../i18n/syncTranslationsFromRedux';
import i18n from '../i18n/i18n';
import { addResourceToi18n, addResourceToi18nSuccess } from '../actions';
import { store } from '../store/store';
import { UPDATE_LANGUAGE_APPLICATION_USER } from '../actions/types';
import codes from '../utils/constants/codes';

const REQUEST_LOGIN = 'requestLogin';
const REQUEST_LOGOUT = 'requestLogout';
const REQUEST_APPLICATION_USER_DETAILS = 'requestApplicationUserDetails';
const REQUEST_APPLICATION_CUSTOMER_FEEDBACK =
    'requestApplicationCustomerFeedback';
const UPDATE_APPLICATION_TERMS_CONSENTED =
    'updateApplicationUserTermsConsented';
const POST_APPLICATION_CUSTOMER_FEEDBACK = 'postApplicationCustomerFeedback';
const REQUEST_APPLICATION_USER_WORDINGS = 'requestApplicationUserWordings';
const REQUEST_UPDATE_LANGUAGE = 'requestUpdateLanguage';
const REQUEST_APPLICATION_USER_REVIEW_CLAIM_WORDINGS =
    'requestApplicationUserReviewClaimWordings';

export function addResourceFileToi18n(sources) {
    const action$ = sources.ACTION.filter(action => {
        return (
            (action.type ===
                ActionTypes.FETCH_APPLICATION_USER_WORDINGS_SUCCESS &&
                store.getState().applicationUser.applicationUserDetails
                    .languageId &&
                store.getState().applicationSettings.apiApplicationSettings
                    .languages) ||
            (action.type ===
                ActionTypes.FETCH_APPLICATION_USER_DETAILS_SUCCESS &&
                store.getState().applicationUser.wordings &&
                store.getState().applicationSettings.apiApplicationSettings
                    .languages) ||
            (action.type === ActionTypes.FETCH_APPLICATION_SETTINGS_SUCCESS &&
                store.getState().applicationUser.applicationUserDetails
                    .languageId &&
                store.getState().applicationUser.wordings)
        );
    }).map(action => {
        store.dispatch(actions.addResourceToi18n());
        syncTranslationsFromRedux(
            store.getState().applicationUser.wordings,
            store.getState().applicationUser.applicationUserDetails.languageId,
            store.getState().applicationSettings.apiApplicationSettings
                .languages,
        );
        return actions.addResourceToi18nSuccess();
    });

    return {
        ACTION: action$,
    };
}
export function addResourceFileToi18nForUnauthorisedUser(sources) {
    const action$ = sources.ACTION.filter(action => {
        return (
            action.type === ActionTypes.FETCH_UNAUTHORISED_WORDINGS_SUCCESS &&
            store.getState().applicationUser.unAuthorisedDetails.languageId &&
            store.getState().applicationSettings.apiApplicationSettings
                .languages &&
            store.getState().applicationUser.wordings
        );
    }).map(action => {
        store.dispatch(actions.addResourceToi18n());
        syncTranslationsFromRedux(
            store.getState().applicationUser.wordings,
            store.getState().applicationUser.unAuthorisedDetails.languageId,
            store.getState().applicationSettings.apiApplicationSettings
                .languages,
        );
        return actions.addResourceToi18nSuccess();
    });

    return {
        ACTION: action$,
    };
}

function refetchClaimLanguageData() {
    // The claim header (form type, stage, state) is rendered straight from
    // API-supplied wording, so an open claim has to be re-read to pick up the
    // new language.
    const openClaimApplicationId = GENERAL_FUNCTIONS.matchClaimIdFromUrl();
    if (openClaimApplicationId) {
        store.dispatch(
            actions.fetchClaimApplicationById(openClaimApplicationId),
        );
    }

    const {
        policyType,
        claimCategory,
        formType,
    } = store.getState().answer.answer;

    const refreshActions = policyType
        ? [
              actions.fetchClaimCategoriesByPolicyType({
                  policyTypeCode: policyType,
              }),
              ...(claimCategory
                  ? [
                        actions.fetchFormTypesByPolicyAndCategoryCode({
                            policyTypeCode: policyType,
                            claimCategoryCode: claimCategory,
                        }),
                        actions.fetchQuestionGroupStepByPolicyTypeAndClaimCategoryAndClaimType(
                            {
                                policyTypeCode: policyType,
                                claimCategoryCode: claimCategory,
                                formTypeCode: formType || '___',
                            },
                        ),
                    ]
                  : []),
          ]
        : [];

    refreshActions.forEach(refreshAction => store.dispatch(refreshAction));
}

export function changeWordingAfterUpdateLanguage(sources) {
    const action$ = sources.ACTION.filter(action => {
        return (
            action.type === ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER_SUCCESS
        );
    }).map(action => {
        // Flip i18n before dispatching anything: the refetches below are
        // localised off `Accept-Language: i18n.language`, and syncTranslations-
        // FromRedux only changes it once the wordings response lands — far too
        // late, so those requests would go out asking for the old language.
        const newLanguageId = action.payload?.request?.send?.languageId;
        const languages =
            store.getState().applicationSettings?.apiApplicationSettings
                ?.languages;
        const newIetf = languages?.find(x => x.id === newLanguageId)?.ietfbcP47;
        if (newIetf) {
            i18n.changeLanguage(newIetf);
        }

        store.dispatch(actions.fetchApplicationUserWordings());
        store.dispatch(actions.fetchApplicationSettings());
        store.dispatch(actions.fetchPolicyTypes());
        store.dispatch(actions.fetchQuestionValidationRule());
        refetchClaimLanguageData();

        return actions.fetchApplicationUserDetails();
    });

    return {
        ACTION: action$,
    };
}

export function requestUpdateLanguage(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const request$ = sources.ACTION.filter(
        action => action.type === ActionTypes.UPDATE_LANGUAGE_APPLICATION_USER,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => {
            return {
                url:
                    GENERAL_FUNCTIONS.getBaseURL() +
                    'api/ApplicationUser/me/change-language',
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
                category: REQUEST_UPDATE_LANGUAGE,
                method: 'PUT',
                send: action.payload,
            };
        });

    let httpResponse$ = sources.HTTP.select(REQUEST_UPDATE_LANGUAGE)
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.updateLanguageApplicationUserSuccess(response)
                : actions.updateLanguageApplicationUserFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}

export function requestApplicationUserDetails(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);
    const request$ = sources.ACTION.filter(
        action => action.type === ActionTypes.FETCH_APPLICATION_USER_DETAILS,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => ({
            url: GENERAL_FUNCTIONS.getBaseURL() + 'api/ApplicationUser/me/info',
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
            category: REQUEST_APPLICATION_USER_DETAILS,
            method: 'GET',
        }));

    let httpResponse$ = sources.HTTP.select(REQUEST_APPLICATION_USER_DETAILS)
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.fetchApplicationUserDetailsSuccess(response)
                : actions.fetchApplicationUserDetailsFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}
export function requestApplicationUserWordings(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const request$ = sources.ACTION.filter(
        action => action.type === ActionTypes.FETCH_APPLICATION_USER_WORDINGS,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => ({
            url:
                GENERAL_FUNCTIONS.getBaseURL() +
                'api/ApplicationUser/me/wordings',
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
            category: REQUEST_APPLICATION_USER_WORDINGS,
            method: 'GET',
        }));

    let httpResponse$ = sources.HTTP.select(REQUEST_APPLICATION_USER_WORDINGS)
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.fetchApplicationUserWordingsSuccess(response)
                : actions.fetchApplicationUserWordingsFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}

export function requestApplicationUserInfoAfterSuccessfulLogin(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const action$ = sources.ACTION.filter(
        action => action.type === ActionTypes.LOGIN_SUCCEEDED,
    )
        .compose(sampleCombine(token$))
        .map(([action, token]) => actions.fetchApplicationUserDetails());

    return {
        ACTION: action$,
    };
}

export function requestLogin(sources) {
    const request$ = sources.ACTION.filter(
        action => action.type === ActionTypes.LOGIN,
    ).map(action => ({
        url: GENERAL_FUNCTIONS.getBaseURL() + 'api/login',
        category: REQUEST_LOGIN,
        method: 'POST',
        send: action.payload,
    }));

    let httpResponse$ = sources.HTTP.select(REQUEST_LOGIN)
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.loginSucceeded(response)
                : actions.loginFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}

export function requestApplicationCustomerFeedback(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const request$ = sources.ACTION.filter(
        action =>
            action.type === ActionTypes.FETCH_APPLICATION_CUSTOMER_FEEDBACK,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => ({
            url:
                GENERAL_FUNCTIONS.getBaseURL() +
                'api/ApplicationUser/me/customer-feedback',
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
            category: REQUEST_APPLICATION_CUSTOMER_FEEDBACK,
            method: 'GET',
        }));

    let httpResponse$ = sources.HTTP.select(
        REQUEST_APPLICATION_CUSTOMER_FEEDBACK,
    )
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.fetchApplicationCustomerFeedbackSuccess(response)
                : actions.fetchApplicationCustomerFeedbackFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}

export function updateApplicationUserTermsConsented(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const request$ = sources.ACTION.filter(
        action =>
            action.type === ActionTypes.UPDATE_APPLICATION_TERMS_CONSENTED,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => {
            const userId = action.payload?.userId;
            const languageCode = action.payload?.languageCode;
            if (!userId) {
                throw new Error('User ID is missing!');
            }

            return {
                url: `${GENERAL_FUNCTIONS.getBaseURL()}api/ApplicationUser/${userId}/update-terms-consented/${languageCode}`,
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
                category: UPDATE_APPLICATION_TERMS_CONSENTED,
                method: 'PUT',
                send: action.payload,
            };
        });

    let httpResponse$ = sources.HTTP.select(UPDATE_APPLICATION_TERMS_CONSENTED)
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response => {
            if (response.status === 200) {
                // Dispatch both the success action and fetch user details
                store.dispatch(actions.fetchApplicationUserDetails());
                store.dispatch(actions.fetchApplicationUserWordings());
                store.dispatch(actions.fetchApplicationSettings());

                // Update language after terms consent succeeds
                const languageCode = response.request?.send?.languageCode;
                if (languageCode) {
                    const languages =
                        store.getState().applicationSettings
                            ?.apiApplicationSettings?.languages;
                    const language = languages?.find(
                        l =>
                            l.ietfbcP47 === languageCode ||
                            l.code === languageCode,
                    );
                    if (language) {
                        store.dispatch(
                            actions.updateLanguageApplicationUser({
                                languageId: language.id,
                            }),
                        );
                    }
                }

                return actions.updateApplicationTermsAndConditionsTermsConsentedSuccess(
                    response,
                );
            } else {
                return actions.updateApplicationTermsAndConditionsTermsConsentedFailed(
                    response,
                );
            }
        });

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}

export function requestApplicationUserReviewClaimWordings(sources) {
    const state$ = sources.STATE;
    const token$ = state$.map(state => state.applicationUser.accessToken);

    const request$ = sources.ACTION.filter(
        action => action.type === ActionTypes.FETCH_UNAUTHORISED_WORDINGS,
    )
        .compose(delay())
        .compose(sampleCombine(token$))
        .map(([action, token]) => {
            const { languageId, claimApplicationId } = action.payload.payload;
            let url =
                GENERAL_FUNCTIONS.getBaseURL() +
                `api/ApplicationUser/review-claim/language/${languageId}`;
            if (claimApplicationId) {
                url += `?claimApplicationId=${claimApplicationId}`;
            }
            return {
                url: url,
                headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
                category: REQUEST_APPLICATION_USER_REVIEW_CLAIM_WORDINGS,
                method: 'GET',
            };
        });

    let httpResponse$ = sources.HTTP.select(
        REQUEST_APPLICATION_USER_REVIEW_CLAIM_WORDINGS,
    )
        .map(response => response.replaceError(err => xs.of(err)))
        .flatten()
        .map(response =>
            response.status === 200
                ? actions.fetchUnauthorisedUserWordingsSuccess(response)
                : actions.fetchUnauthorisedUserWordingsFailed(response),
        );

    return {
        ACTION: httpResponse$,
        HTTP: request$,
    };
}
